import ControlViewModel from '@/ViewModels/ControlViewModel';
import FancyTreeViewModel from '@/ViewModels/Controls/FancyTreeViewModel';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import TreeViewItemViewModel from '@/ViewModels/Controls/TreeViewItemViewModel';
import TextBoxViewModelT from '@/ViewModels/Controls/TextBoxViewModel';
import TreeViewItemViewModelT from '@/ViewModels/Controls/TreeViewItemViewModel';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import NavigationService from '@/Management/NavigationService';
import NavigationHelper from '@/Management/NavigationHelper';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import Utilities from '@/Utility/Utilities';
import GlobalConfiguration from "@/Management/GlobalConfiguration";
export default class TreeViewViewModel extends ControlViewModel {
    public tree: FancyTreeViewModel;
    public isInSearchMode: KnockoutObservable<boolean>;
    public searchString: TextBoxViewModel;
    public hasSearchFeature: KnockoutObservable<boolean>;
    /**
    * Enthält die Kindelemente
    * @type {[TreeViewItemViewModel]}
    */
    public items: KnockoutObservableArray<TreeViewItemViewModel>;
    /**
    * Gibt an, ob die enthaltenen TreeViewItems nach Namen sortiert werden sollen
    */
    public isSorted: any;
    /**
    * Gibt an, ob Checkboxen angezeigt werden sollen
    */
    public hasCheckboxes: any;
    /**
    * Gibt an, ob Default-Items hinzugefügt werden sollen (Startseite, Anwendungssuche)
    */
    public hasDefaultItems: any;
    /**
    * Gibt an, ob Favoriten hinzugefügt werden sollen
    */
    public hasFavoriteItems: any;
    /**
    * Ein Handler, der ausgeführt wird, sobald die Elemente geladen sind
    */
    public onLoad: any;
    /**
    * Gibt eine konvertierte Liste der Items zurück (sortiert, falls Sortierung aktiviert ist)
    */
    public sortedItems: any;
    public hasCollapseAllFeature: any;
    public hasSearchInput: any;
    private dataSource: any;
    private TextBoxViewModel: any;
    constructor(params: any) {
        super(params);
        this.items = this.observableOrDefault(params.items, []) as KnockoutObservableArray<TreeViewItemViewModel>;
        this.isSorted = this.observableOrDefault(params.isSorted, false);
        this.hasCheckboxes = this.observableOrDefault(params.hasCheckboxes, false);
        this.hasDefaultItems = this.observableOrDefault(params.hasDefaultItems, false);
        this.hasFavoriteItems = this.observableOrDefault(params.hasFavoriteItems, false);
        this.onLoad = this.handlerOrDefault(params.onLoad);
        this.sortedItems = this.defineComputed(() => {
            let items = this.items();
            let isSorted = this.isSorted();
            if (isSorted === true) {
                let sortedItems = [];
                ko.ignoreDependencies(() => {
                    sortedItems = _.sortBy(items, (treeViewItem) => {
                        return treeViewItem.link.label.caption();
                    });
                });
                return sortedItems;
            }
            return items;
        });
        this.dataSource = this.observableOrDefault([]);
        this.defineSubscription(this.sortedItems, (_items) => {
            this.dataSource(_.isEmpty(_items) ?
                [] :
                this.convertItemsToNodes(_items)
            );
        })(this.sortedItems());
        this.tree = new FancyTreeViewModel({
            parentLocator: this.locator,
            locator: 'fancytree',
            nodes: this.dataSource,
            renderCheckboxes: false,
            renderIcons: false,
            autoScroll: true,
            extensions: [],
            selectMode: 1 // single select
        });
        this.hasSearchFeature = this.observableOrDefault(params.hasSearchFeature, true);
        this.isInSearchMode = this.observableOrDefault(false);
        this.hasCollapseAllFeature = this.observableOrDefault(params.hasCollapseAllFeature, false);
        this.TextBoxViewModel = ComponentManager.getViewModel<typeof TextBoxViewModelT>('TextBoxViewModel');
        this.searchString = new this.TextBoxViewModel({
            isFocusable: true
        });
        this.searchString.value.extend({ rateLimit: { timeout: 100, method: "notifyWhenChangesStop" } });
        this.hasSearchInput = this.defineComputed(() => !String.isNullOrEmpty(this.searchString.value()))
        this.defineSubscription(this.searchString.value, (searchString) => {
            let captionMatchesSearchString = (caption: any): any => {
                return Utilities.isBlank(searchString) ||
                    Utilities.contains(caption.toLowerCase(), searchString.toLowerCase());
            };
            let shouldBeVisibleItem = (item: any): any => {
                let caption = item.link.label.caption();
                let children = _.isFunction(item.items) ? item.items() : item.items;
                return captionMatchesSearchString(caption) || anyChildMatchesSearchString(children);
            };

            let anyChildMatchesSearchString = (children: any): any => {
                return !_.isEmpty(children) && children.some((child) => {
                    return shouldBeVisibleItem(child);
                });
            };
            let setIsVisibleState = (item: any): any => {
                let alreadyVisible = item.isVisible();
                let shouldBeVisible = shouldBeVisibleItem(item);
                item.isVisible(shouldBeVisible);
                if (alreadyVisible === shouldBeVisible) {
                    item.isVisible.valueHasMutated();
                }
            };
            let setIsVisibleStates = (items: any): any => {
                items.forEach((item) => {
                    setIsVisibleState(item);
                });
                items.forEach((item) => {
                    let children = _.isFunction(item.items) ? item.items() : item.items;
                    setIsVisibleStates(children);
                });
            };
            this.isInSearchMode(Utilities.isNotBlank(searchString));
            setIsVisibleStates(this.items());
        });
        ControlViewModel.checkProperties(this, params);
    }
    /**
    * Erzeugt ein neues, geschachteltes TreeViewViewModel für den Navigationsbaum
    */
    public static parseUserNavigationTree(parentLocator: string | KnockoutObservable<string>): TreeViewViewModel {
        let TreeViewItemViewModel = ComponentManager.getViewModel<typeof TreeViewItemViewModelT>('TreeViewItemViewModel');
        let LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
        let ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
        let LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
        let createChildItems = (items, parentLocator) => {
            let menuItems = [];
            if (items != null) {
                for (let i = 0; i < items.length; i++) {
                    let item = createItem(items[i], parentLocator);
                    menuItems.push(item);
                }
            }
            return menuItems;
        }
        let createItem = (item, parentLocator) => {
            let itemLocator = '[' + item.name + ']';
            let locator = ko.computed(() => {
                return parentLocator() + '.' + itemLocator;
            });
            let items = createChildItems(item.childNodes, locator);
            let treeViewItem = new TreeViewItemViewModel({
                parentLocator: parentLocator,
                locator: itemLocator,
                isExpanded: item.isExpanded === true,
                link: new LinkViewModel({
                    parentLocator: parentLocator,
                    locator: itemLocator + '.link',
                    image: new ImageViewModel({
                        parentLocator: parentLocator,
                        locator: itemLocator + '.link.image',
                        source: item.iconUrl,
                        width: 16,
                        height: 16
                    }),
                    label: new LabelViewModel({
                        parentLocator: parentLocator,
                        locator: itemLocator + '.link.label',
                        caption: item.caption,
                        ergName: item.ergName
                    }),
                    onClick: () => {
                        TreeViewViewModel.onItemClick(item);
                    }
                }),
                favoriteAddress: item.favoriteAddress,
                items: items
            });
            return treeViewItem;
        }
        if (String.isNullOrEmpty(parentLocator as string)) {
            parentLocator = 'user-navigation-tree';
        }
        let items = ko.computed(() => {
            let menuItems = [];
            let menus = RADApplication.getInstance().user.menus();
            let navigationTree = menus.userNavigationTree;
            if (navigationTree != null) {
                for (let i = 0; i < navigationTree.childNodes.length; i++) {
                    let item = createItem(navigationTree.childNodes[i], parentLocator);
                    menuItems.push(item);
                }
            }
            return menuItems;
        });
        let treeVM = new TreeViewViewModel({
            parentLocator: parentLocator,
            locator: '_root_',
            items: items,
            hasDefaultItems: false,
            hasFavoriteItems: false,
            isSorted: false,
            hasCollapseAllFeature: true
        });
        treeVM.addComputed(items);
        return treeVM;
    }
    /**
    * Erzeugt ein neues, geschachteltes TreeViewViewModel für den Favoritenbaum
    */
    public static parseUserFavoriteTree(searchIsInFavorites?: boolean): TreeViewViewModel {
        let TreeViewItemViewModel = ComponentManager.getViewModel<typeof TreeViewItemViewModelT>('TreeViewItemViewModel');
        let LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
        let ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
        let LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
        let parentLocator = ko.observable('');
        if (String.isNullOrEmpty(parentLocator())) {
            parentLocator('user-favorite-tree');
        }
        let createItem = (item) => {
            let treeViewItem = new TreeViewItemViewModel({
                parentLocator: parentLocator,
                locator: item.name,
                link: new LinkViewModel({
                    parentLocator: parentLocator,
                    locator: item.name + '.link',
                    image: new ImageViewModel({
                        parentLocator: parentLocator,
                        locator: item.name + '.link.image',
                        source: item.iconUrl
                        // TODO: Alternativetext
                    }),
                    label: new LabelViewModel({
                        parentLocator: parentLocator,
                        locator: item.name + '.link.label',
                        ergName: item.ergName,
                        tryToLoad: false
                    }),
                    onClick: () => {
                        TreeViewViewModel.onItemClick(item);
                    }
                }),
                favoriteAddress: item.favoriteAddress
            });
            return treeViewItem;
        }
        let items = ko.computed(() => {
            let menuItems = [];
            if (searchIsInFavorites !== false) {
                menuItems.push(createItem({
                    name: 'search',
                    ergName: 'SEARCH_TITLE',
                    iconUrl: '$$TEMPLATE_HANDLER$$?TemplateName=$$TEMPLATE_STYLE$$&ImageName=index_view.png',
                    action: 'CUSTOM_ACTION',
                    actionCallback: () => {
                        NavigationHelper.openSearch();
                    }
                }));
            }
            let menus = RADApplication.getInstance().user.menus();
            let favoriteTree = menus.userFavoriteTree;
            if (favoriteTree != null) {
                for (let i = 0; i < favoriteTree.childNodes.length; i++) {
                    let item = createItem(favoriteTree.childNodes[i]);
                    menuItems.push(item);
                }
            }
            let favorites = RADApplication.getInstance().user.getUserSpecificData('RAD_FAVOURITESITES', {})();
            let i = 0;
            for (const link of Object.keys(favorites)) {
                let favorite = createItem({
                    name: 'favorite.' + String(i++),
                    favoriteAddress: link,
                    ergName: favorites[link],
                    iconUrl: `$$TEMPLATE_HANDLER$$?TemplateName=$$TEMPLATE_STYLE$$&ImageName=${GlobalConfiguration.inlineImages.FAVOURITE_ACTIVE}`
                });
                menuItems.push(favorite);
            }
            return menuItems;
        });
        let treeVM = new TreeViewViewModel({
            parentLocator: parentLocator,
            locator: '_root_',
            items: items,
            hasDefaultItems: true,
            hasFavoriteItems: true,
            isSorted: true
        });
        treeVM.addComputed(items);
        return treeVM;
    }
    public static onItemClick(item: any): any {
        if (item.action === 'CUSTOM_ACTION') {
            item.actionCallback();
        }
        else if (!String.isNullOrEmpty(item.favoriteAddress)) {
            NavigationService.getInstance().navigateToUrl(item.favoriteAddress);
        }
        else if (!String.isNullOrEmpty(item.action)) {
            NavigationService.getInstance().loadViewFactory(item.action);
        }
        else if (!String.isNullOrEmpty(item.url)) {
            NavigationService.getInstance().navigateToUrl(item.url);
        }
        else if (item.isListView === true) {
            NavigationHelper.openListViewById(item.viewPDOID);
        }
        else if (!String.isNullOrEmpty(item.pdTypeName)) {
            NavigationHelper.openDetailView('EDIT', item.pdTypeName);
        }
        else if (!String.isNullOrEmpty(item.viewExtensionFactory)) {
            NavigationHelper.openExtensionView(item.viewExtensionFactory, item.ergName);
        }
    }
    private convertItemsToNodes(_items: any): any {
        return _items.map((_item) => {
            return {
                key: _item.identifier,
                title: "",
                children: this.convertItemsToNodes(_item.items ? _item.items() : []),
                treeViewItem: _item,
                data: { isExpandedObs: _item.isExpanded }
            };
        });
    }
}

ComponentManager.setViewModel('TreeViewViewModel', TreeViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');